import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { Component, effect, HostListener, inject, untracked, viewChild, ViewChild, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { fb, goBack, isAllowed } from '../../../../shared/shared.global-service';
import { ContextMenu } from '@openng/optimus-ui/contextmenu';
import { MenuItem, PrimeTemplate } from '@openng/optimus-ui/api';
import { Listbox, ListboxChangeEvent } from '@openng/optimus-ui/listbox';
import { FilterBuilder } from '../../../helper/filter.builder';
import { AnamnesisObject } from '@libs/types/src/services/object-type/anamnesis/anamnesis.object';
import { bloodTypeEnumOptions } from '@libs/types/src/common/enum/blood-type.enum';
import { AnamnesisCreateInput } from '@libs/types/src/services/input-type/anamnesis/anamnesis.create-input';
import { FormsModule, Validators } from '@angular/forms';
import { isInvalid } from '../../../../shared/helpers/validate';
import { AnamnesisUpdateInput } from '@libs/types/src/services/input-type/anamnesis/anamnesis.update-input';
import { dateify } from '../../../../shared/helpers/date.helper';
import {
    InputWysiwygComponent,
    validatorWysiwygRequired,
} from '../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BaseServicePrintDialogComponent } from '../../../../../components/medical-service/print/base-service-print-dialog/base-service-print-dialog.component';
import { FolderAnamnesisPageStore } from './folder-anamnesis-page.store';
import { checkOk, contentGlobalSuccess } from '../../../content.error';
import { folderAnamnesisFrag } from './folder-anamnesis-page.query';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import {
    disableFormControls,
    formAddText,
} from '../../../../../utils/form.utils';
import { generateFilename } from '../../../../../utils/files.utils';
import { getCurrentInsurance } from '../../../../../utils/insurance.utils';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { ServicePatternWysiwygComponent } from '../../../components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { InputFulltextComponent } from '../../../../shared/components/inputs/input-fulltext/input-fulltext.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { DiagnosisSelectInputComponent } from '../../../../../components/medical-service/diagnosis-select-input/diagnosis-select-input.component';
import { ServiceDoctorSelectInputComponent } from '../../../../../components/medical-service/element/service-doctor-select-input/service-doctor-select-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import {
    InsuranceCompanySelectInputComponent,
} from '../../../../../components/medical-service/element/insurance-company-select-input/insurance-company-select-input.component';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { UserChipComponent } from '../../../../../components/kit/user-chip/user-chip.component';
import { wysiwygPatterGroupsMedicalFolder } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { PersonContactUpsertDialogComponent } from '../../../components/person/person-contact-upsert-dialog/person-contact-upsert-dialog.component';
import { ContactTypeEnum } from '@libs/types/src/common/enum/contact-type.enum';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import {
    BackgroundActionType,
    ClientDocumentExportKind,
} from '@libs/types/src/notifications/types/background-action.types';
import { FolderAnamnesisPageHistoryState } from './folder-anamnesis-page.types';

@Component({
    selector: 'app-folder-anamnesis-page',
    templateUrl: 'folder-anamnesis-page.component.html',
    styleUrl: 'folder-anamnesis-page.component.scss',
    providers: [FolderAnamnesisPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputFulltextComponent,
        ButtonComponent,
        DiagnosisSelectInputComponent,
        ServiceDoctorSelectInputComponent,
        SelectInputComponent,
        InsuranceCompanySelectInputComponent,
        CheckInputComponent,
        DateInputComponent,
        ServicePatternWysiwygComponent,
        InputWysiwygComponent,
        Listbox,
        TranslatePipe,
        PrimeTemplate,
        FormsModule,
        UserChipComponent,
        ContextMenu,
        Tooltip,
    ],
})
export class FolderAnamnesisPageComponent extends SubscriptionComponent {
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(FolderAnamnesisPageStore);
    protected readonly dialogService = inject(BaseDialogService);
    protected readonly translate = inject(TranslateService);
    private readonly backgroundTaskLauncher = inject(
        BackgroundTaskLaunchService,
    );
    private readonly goBackAfterSave = Boolean(
        (history.state as FolderAnamnesisPageHistoryState | null)?.goBackAfterSave,
    );

    protected prefix = 'anamnesis.';

    public readonly wysiwygService = viewChild<ServicePatternWysiwygComponent>(
        ServicePatternWysiwygComponent,
    );
    public readonly wysiwygs = viewChildren<InputWysiwygComponent>(
        InputWysiwygComponent,
    );
    private readonly doctorSelectInput = viewChild.required(
        ServiceDoctorSelectInputComponent,
    );

    protected selectedAnamnesis: AnamnesisObject | null = null;

    @ViewChild('contextMenu') ctx!: ContextMenu;
    protected contextMenuItemsAll: MenuItem[] = [
        {
            label: 'update',
            icon: 'pi pi-file-edit',
            command: () => {
                this.update();
            },
        },
        {
            label: 'delete',
            icon: 'pi pi-times',
            command: () => {
                this.delete();
            },
        },
    ];
    protected contextMenuItems = this.contextMenuItemsAll;
    protected contextItem: number = 0;

    protected form = fb.group({
        selectedAnamnesisData: fb.control<string>(''),
        search: fb.control<string>(''),

        isHiv: fb.control<boolean>(false),
        isRwe: fb.control<boolean>(false),
        isHbsag: fb.control<boolean>(false),
        dateFrom: fb.control<Date | null>(null, Validators.required),
        dateTo: fb.control<Date | null>(null),
        anamnesis: fb.control<string>('', {
            nonNullable: true,
            validators: [validatorWysiwygRequired],
        }),
        operation: fb.control<string>('', { nonNullable: true }),
        alergies: fb.control<string>('', { nonNullable: true }),
        symptoms: fb.control<string>('', { nonNullable: true }),
        invasives: fb.control<string>('', { nonNullable: true }),
        importantNote: fb.control<string>('', { nonNullable: true }),
        note: fb.control<string>('', { nonNullable: true }),
        socialAnamnesis: fb.control<string>('', { nonNullable: true }),
        nurseRecommendations: fb.control<string>('', { nonNullable: true }),
        statusPraesens: fb.control<string>('', { nonNullable: true }),
        warning: fb.control<string>('', { nonNullable: true }),
        vaccine: fb.control<string>('', { nonNullable: true }),
        implants: fb.control<string>('', { nonNullable: true }),
        medication: fb.control<string>('', { nonNullable: true }),
        wishes: fb.control<string>('', { nonNullable: true }),
        bloodType: fb.control<string>('', { nonNullable: true }),
        insuranceAbbr: fb.control<string | null>(null),
        diagnosis: fb.control<string | null>(null),
        secondaryDiagnoses: fb.control<string[]>([]),
        doctorId: fb.control<string | null>(null),
    });

    constructor() {
        super();

        effect(() => {
            disableFormControls(
                this.form,
                !this.store.isAllowed('folderRecord.create')() ||
                    this.folderStore.isTerminated(),
            );
        });

        this.store.onSuccess(
            async () => this.loadAnamneses(this.store.anamnesisFolderIds()),
            'createAnamnesis',
            'updateAnamnesis',
            'deleteAnamnesis',
        );

        effect(() => {
            const anamneses = this.store.anamneses();

            if (anamneses.length > 0) {
                this.selectedAnamnesis = anamneses[0];
                this.updateForm();
            } else {
                const currentInsurance = getCurrentInsurance(
                    this.store.folder(),
                );

                this.form.patchValue({
                    insuranceAbbr: currentInsurance?.insuranceAbbr,
                });
            }
        });

        this.contextMenuItemsAll.forEach((it) => {
            it.label = this.translate.instant(
                `database.persons.epicrisis.${it.label}`,
            );
        });

        effect(() => {
            const folderIds = this.store.anamnesisFolderIds();
            if (folderIds.length) this.loadAnamneses(folderIds);
        });
    }

    public listChange(index: ListboxChangeEvent): void {
        if (index.value) {
            this.selectedAnamnesis = this.store.anamneses()[index.value.index];
            this.updateForm();
        }
    }

    protected async save(): Promise<void> {
        if (isInvalid(this.form)) return;

        if (!this.store.folder()) return;
        const res = await this.store.api.createAnamnesis({
            folderId: this.store.folder()?.id,
            dateFrom: this.form.value.dateFrom?.toISOString() || null,
            dateTo: this.form.value.dateTo?.toISOString() || null,
            isHiv: this.form.value.isHiv || false,
            isRwe: this.form.value.isRwe || false,
            isHbsag: this.form.value.isHbsag || false,
            anamnesis: this.form.value.anamnesis || '',
            operation: this.form.value.operation || '',
            alergies: this.form.value.alergies || '',
            symptoms: this.form.value.symptoms || '',
            invasives: this.form.value.invasives || '',
            importantNote: this.form.value.importantNote || '',
            note: this.form.value.note || '',
            socialAnamnesis: this.form.value.socialAnamnesis || '',
            nurseRecommendations: this.form.value.nurseRecommendations || '',
            statusPraesens: this.form.value.statusPraesens || '',
            warning: this.form.value.warning || '',
            vaccine: this.form.value.vaccine || '',
            implants: this.form.value.implants || '',
            medication: this.form.value.medication || '',
            wishes: this.form.value.wishes || '',
            bloodType: this.form.value.bloodType || '',
            insuranceAbbr: this.form.value.insuranceAbbr || '',
            diagnosis: this.form.value.diagnosis || '',
            secondaryDiagnoses:
                this.form.value.secondaryDiagnoses?.map((it: string) => ({
                    code: it,
                })) || [],
            selectedAnamnesisData: undefined,
            search: undefined,
            doctorId: this.form.value.doctorId,
        } as unknown as AnamnesisCreateInput);

        this.wysiwygService()?.resetBackupValues();
        this.wysiwygs()?.forEach((it) => it.resetBackupValues());

        if (checkOk(res) && this.goBackAfterSave) goBack();
    }

    protected selectedAnamnesisChange(selectedSearch: any): void {
        this.selectedAnamnesis = selectedSearch as any;
        this.updateForm();
    }

    protected async delete() {
        if (this.contextItem) {
            await this.store.api.deleteAnamnesis({ id: this.contextItem });
            contentGlobalSuccess.next('common.deleted');
        }
    }

    protected async update() {
        if (this.contextItem) {
            const res = await this.store.api.updateAnamnesis({
                id: this.contextItem,
                ...this.form.value,
                ...{
                    secondaryDiagnoses:
                        this.form.value.secondaryDiagnoses?.map(
                            (it: string) => ({ code: it }),
                        ) || [],
                },
                selectedAnamnesisData: undefined,
                search: undefined,
            } as unknown as AnamnesisUpdateInput);

            this.wysiwygService()?.resetBackupValues();
            this.wysiwygs()?.forEach((it) => it.resetBackupValues());

            if (checkOk(res, 'update') && this.goBackAfterSave) goBack();
        }
    }

    protected addNewDoctor(): void {
        const ref = this.dialogService.open(
            PersonContactUpsertDialogComponent,
            {
                header: this.translate.instant(
                    'database.persons.contacts.addHeader',
                ),
                data: {
                    type: ContactTypeEnum.doctor,
                    typeDisabled: true,
                },
                styleClass: 'p-dialog-width-60',
            },
        );

        ref?.onClose.subscribe(
            (result?: { userId?: string; refresh?: boolean }) => {
                if (!result?.userId || !result.refresh) return;

                this.doctorSelectInput().forceRefresh.set({});
                this.form.controls.doctorId.setValue(result.userId);
            },
        );
    }

    protected print() {
        this.dialogService.open(BaseServicePrintDialogComponent, {
            header: this.translate.instant('common.print'),
            data: {
                serviceId: this.store.serviceId(),
                archive: true,
                onStart: (value: any) =>
                    this.backgroundTaskLauncher.start({
                        type: BackgroundActionType.CLIENT_DOCUMENT_EXPORT,
                        payload: {
                            folderId: this.store.folder()?.id || 0,
                            kind: ClientDocumentExportKind.ANAMNESIS,
                            input: {
                                filename: generateFilename(
                                    this.prefix + 'label',
                                ),
                                id: this.selectedAnamnesis?.id || 0,
                                headerId: value.headerId || 0,
                                footerId: value.footerId || 0,
                                archive: value.archive || false,
                            },
                        },
                        alreadyRunningMessage:
                            'person.documentExport.backgroundTask.messages.alreadyRunning',
                        failedMessage:
                            'person.documentExport.backgroundTask.messages.startFailed',
                    }),
            },
        });
    }

    @HostListener('document:contextmenu')
    public rightClickAny() {
        this.ctx.hide();
    }

    @HostListener('document:click')
    public leftClickAny() {
        this.ctx.hide();
    }

    public rightClick(
        menu: ContextMenu,
        event: MouseEvent,
        item: AnamnesisObject,
    ) {
        this.contextItem = item.id;
        menu.hide();
        event.preventDefault();
        this.contextMenuItems = this.contextMenuItemsAll.slice(
            this.selectedAnamnesis?.id === item.id ? 0 : 1,
        );
        setTimeout(() => {
            menu.toggle(event);
        }, 1);
    }

    private updateForm(): void {
        if (this.selectedAnamnesis) {
            untracked(() => {
                this.form.patchValue({
                    ...this.selectedAnamnesis,
                    dateFrom: dateify(this.selectedAnamnesis?.dateFrom),
                    dateTo: dateify(this.selectedAnamnesis?.dateTo),
                    diagnosis: this.selectedAnamnesis?.diagnosis?.code || null,
                    secondaryDiagnoses:
                        this.selectedAnamnesis?.secondaryDiagnoses?.map(
                            (it) => it.code,
                        ) || [],
                } as any);
            });
        } else {
            this.form.reset();
        }
    }

    private async loadAnamneses(folderIds: number[]): Promise<void> {
        const filter = new FilterBuilder();
        filter.where('folderId', folderIds, 'IN');
        filter.sort('updatedAt', 'DESC');
        filter.limit(99);

        await this.store.api
            .getAnamneses(filter.filter(), folderAnamnesisFrag)
            .then();
    }

    protected readonly bloodTypeEnumOptions = bloodTypeEnumOptions;
    protected readonly isAllowed = isAllowed;
    protected readonly ServicePatternStructureUsageEnum =
        ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly wysiwygPatterGroupsMedicalFolder =
        wysiwygPatterGroupsMedicalFolder;
}
